import type { BadgeView, ProgressCount, TrainerCardView } from '../../shared/protocol.ts';
import { $, el } from '../common/dom.ts';
import { badgeIcon, icon } from '../common/icons.ts';
import { Modal } from '../common/modal.ts';
import type { IconName } from '../../shared/icons.ts';
import { creatureText } from '../../shared/game-config.ts';

const dateFormat = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

/** Une ligne de progression : fait / total et sa barre. */
function progressRow(iconName: IconName, label: string, { done, total }: ProgressCount): HTMLElement {
  const bar = el('span', { className: 'progress-bar' }, el('span'));
  (bar.firstElementChild as HTMLElement).style.width = `${total ? Math.min(100, (100 * done) / total) : 0}%`;
  return el('div', { className: 'trainer-progress-row' },
    icon(iconName), el('span', { textContent: creatureText(label) }), bar, el('b', { textContent: `${done}/${total}` }));
}

/**
 * Carte du dresseur : badges (obtenus en couleur, les autres en silhouette) et progression dans le jeu.
 * La sienne depuis le menu (avec l'accès aux succès et à l'aide), celle d'un autre joueur en l'examinant.
 */
export class TrainerCardPanel extends Modal {
  private readonly title = $('.trainer-card-name', this.root);
  private readonly since = $('.trainer-card-since', this.root);
  private readonly body = $('.trainer-card-body', this.root);
  private readonly actions = $('.trainer-card-actions', this.root);
  private readonly load: (playerId: number | null) => Promise<TrainerCardView | null>;

  constructor(load: (playerId: number | null) => Promise<TrainerCardView | null>) {
    super('#trainer-card');
    this.load = load;
    $('#btn-trainer-card').addEventListener('click', () => this.toggle());
    // Succès et aide s'ouvrent à la place de la carte (leurs fenêtres écoutent aussi ces boutons).
    for (const button of this.actions.querySelectorAll('button')) button.addEventListener('click', () => this.close());
  }

  protected override reveal(): Promise<void> {
    return this.show();
  }

  /** Sans joueur : sa propre carte. */
  async show(player?: { id: number; username: string }): Promise<void> {
    this.open();
    const current = this.startLoad();
    this.title.textContent = player ? `Carte de ${player.username}` : 'Carte du dresseur';
    this.since.textContent = '';
    this.actions.hidden = !!player;
    this.body.replaceChildren(el('p', { className: 'muted', textContent: 'Chargement…' }));
    const card = await this.load(player?.id ?? null).catch(() => null);
    if (!current()) return;
    if (!card) {
      this.body.replaceChildren(el('p', { className: 'muted', textContent: 'Carte indisponible.' }));
      return;
    }
    const own = !player;
    this.title.textContent = own ? 'Ma carte du dresseur' : `Carte de ${card.username}`;
    this.since.textContent = `${own ? '' : `${card.username} · `}Dresseur depuis le ${dateFormat.format(card.since)}`;
    const earned = card.badges.filter((badge) => badge.earnedAt !== null).length;
    this.body.replaceChildren(
      el('h3', {}, icon('badge'), ' Badges ', el('span', { className: 'badge-count', textContent: card.badges.length ? `${earned}/${card.badges.length}` : '' })),
      el('div', { className: 'badge-case' }, ...(card.badges.length
        ? card.badges.map((badge) => this.badgeSlot(badge, own))
        : [el('p', { className: 'empty muted', textContent: 'Aucun badge dans ce monde pour l\'instant.' })])),
      el('h3', {}, icon('ranking'), ' Progression ', el('span', { className: 'badge-count', textContent: `${card.progress} %` })),
      el('div', { className: 'trainer-progress' },
        progressRow('quests', 'Quêtes terminées', card.quests),
        progressRow('capture', 'Espèces capturées', card.caught),
        progressRow('map', 'Lieux visités', card.maps),
        el('div', { className: 'trainer-progress-row' }, icon('duel'), el('span', { textContent: 'Duels gagnés' }), el('span'), el('b', { textContent: String(card.duelsWon) }))),
    );
  }

  private badgeSlot(badge: BadgeView, own: boolean): HTMLElement {
    const owned = badge.earnedAt !== null;
    const slot = el('span', {
      className: `badge-slot ${owned ? '' : 'locked'}`,
      title: badge.earnedAt !== null
        ? `${badge.name} — obtenu le ${dateFormat.format(badge.earnedAt)}\n${creatureText(badge.description)}`
        : own ? `${badge.name} : battez ${badge.champion ?? 'le champion'} pour l'obtenir` : `${badge.name} : pas encore obtenu`,
    });
    slot.append(owned ? badgeIcon(badge.icon) : '?');
    if (owned) slot.style.setProperty('--c', badge.color);
    return slot;
  }
}
